'use client';

import { transferLimitLabel } from '@/lib/fact/labels';

interface Props {
  /** 전매제한 «개월»(정수) — 호출부가 effectiveTransferMonths 로 가드한 값. 표기는 「6개월」「1년」「1년 6개월」. */
  transferLimitMonths?: number | null;
  residenceYears?: number | null;
  rewinLimitYears?: number | null;
  isSpeculativeZone?: boolean | null;
  isRegulatedArea?: boolean | null;
  isPriceLimit?: boolean | null;
  loanRate?: string | null;
  // 해제 예정일 계산용
  contractDate?: string | null; // cntrct_cncls_bgnde
}

type Level = 'danger' | 'warning' | 'safe' | 'info';

function getBadgeStyle(level: Level) {
  const map = {
    danger: { bg: 'rgba(239,68,68,0.1)', color: 'var(--accent-red)', border: 'rgba(239,68,68,0.2)' },
    warning: { bg: 'rgba(245,158,11,0.1)', color: 'var(--warning)', border: 'rgba(245,158,11,0.2)' },
    safe: { bg: 'rgba(34,197,94,0.1)', color: 'var(--accent-green)', border: 'rgba(34,197,94,0.2)' },
    info: { bg: 'rgba(59,130,246,0.1)', color: 'var(--brand)', border: 'rgba(59,130,246,0.2)' },
  };
  return map[level];
}

function calcReleaseDate(contractDate: string | null | undefined, months: number): string | null {
  if (!contractDate) return null;
  try {
    const d = new Date(contractDate);
    d.setMonth(d.getMonth() + months);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
  } catch { return null; }
}

export default function RegulationBadges(props: Props) {
  const items: { label: string; value: string; level: Level }[] = [];

  // 전매제한
  const tlLabel = transferLimitLabel(props.transferLimitMonths);
  if (tlLabel && props.transferLimitMonths) {
    const release = calcReleaseDate(props.contractDate, props.transferLimitMonths);
    const level: Level = props.transferLimitMonths >= 60 ? 'danger' : props.transferLimitMonths >= 36 ? 'warning' : 'safe';
    items.push({
      label: '전매제한',
      value: `${tlLabel}${release ? ` (해제: ${release})` : ''}`,
      level,
    });
  }

  // 거주의무
  if (props.residenceYears != null && props.residenceYears > 0) {
    const level: Level = props.residenceYears >= 3 ? 'warning' : 'safe';
    items.push({ label: '거주의무', value: `${props.residenceYears}년`, level });
  }

  // 재당첨
  if (props.rewinLimitYears != null && props.rewinLimitYears > 0) {
    const level: Level = props.rewinLimitYears >= 7 ? 'danger' : 'warning';
    items.push({ label: '재당첨 제한', value: `${props.rewinLimitYears}년`, level });
  }

  // 중도금 대출
  if (props.loanRate) {
    const isInterestFree = props.loanRate.includes('무이자');
    items.push({
      label: '중도금 대출',
      value: props.loanRate,
      level: isInterestFree ? 'safe' : 'info',
    });
  }

  if (items.length === 0) return null;

  /* CPL A2 — 「투기과열지구」·「조정대상지역」 칩을 «걷었다» (2026-09-23).
   *
   * ⛔ 원천 컬럼이 규제를 말하고 있지 않았다. 실측(`apt_subscriptions` 2,884행):
   *      `is_speculative_zone = true` **2,491** (86%)
   *        그중 `is_regulated_area = false` 1,840 · 수도권 «밖» 1,403
   *    투기과열지구는 실제로 손에 꼽는 지역이다. 86%가 투기과열지구인 나라는 없다 —
   *    이 열은 규제 여부가 아니라 «공고 서식의 어떤 칸» 을 담고 있다(무엇인지는 미확정).
   * ⛔ 그 값으로 「✓ 투기과열지구」를 찍으면 비규제 현장 1,400여 곳에 규제 딱지가 붙는다.
   *    전매·거주·재당첨 연수를 바로 위에서 «사실대로» 내고 있는 화면에서, 그 옆의 틀린
   *    딱지는 나머지 사실까지 의심하게 만든다.
   * ⚠️ 컬럼·프롭은 남긴다(파서·DB 무접촉). 열의 «의미» 가 확정되면 그때 되살린다 —
   *    그건 데이터 판정이지 렌더 판정이 아니다.
   * ⚠️ 「분양가상한제」는 남는다. `is_price_limit` 은 적용/미적용을 둘 다 표시하고(양성·음성
   *    모두 말한다) 공고 원문에 그 칸이 실재한다.
   */
  const tags: { label: string; active: boolean }[] = [];
  if (props.isPriceLimit != null) tags.push({ label: '분양가상한제', active: !!props.isPriceLimit });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((item, i) => {
        const s = getBadgeStyle(item.level);
        return (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 12px', borderRadius: 'var(--radius-md)',
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
          }}>
            <span style={{
              fontSize: 12, padding: '3px 8px', borderRadius: 'var(--radius-sm)', fontWeight: 500,
              background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            }}>
              {item.label}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: s.color }}>
              {item.value}
            </span>
          </div>
        );
      })}
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
          {tags.map((tag, i) => (
            <span key={i} style={{
              fontSize: 12, padding: '3px 10px', borderRadius: 'var(--radius-sm)', fontWeight: 600,
              background: tag.active ? 'rgba(139,92,246,0.1)' : 'var(--bg-hover)',
              // ⚠️ #8B5CF6 은 흰 배경 대비 4.23 으로 하한에 못 미쳤다. 같은 보라 계열
              //    기존 토큰 --accent-purple(#7C3AED)로 5.70. 새 토큰은 만들지 않았다.
              color: tag.active ? 'var(--accent-purple)' : 'var(--text-tertiary)',
              border: `1px solid ${tag.active ? 'rgba(139,92,246,0.2)' : 'var(--border)'}`,
            }}>
              {tag.active ? '✓ ' : ''}{tag.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
